import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import {
  assignContactTag,
  getCachedOrgTags,
  peekCachedOrgTags,
  unassignContactTag,
} from '../../api/tags'
import {
  enrichContactTags,
  getTagChipStyle,
  normalizeContactTags,
  slugifyTagName,
} from '../../utils/contactTags'
import normalizeSearchText from '../../utils/normalizeSearchText'

function AssignTagPopover({
  tenantId,
  contactId,
  assignedTags,
  onAssigned,
  onClose,
  anchorRef,
  variant,
}) {
  const { t } = useTranslation()
  const popoverRef = useRef(null)
  const [search, setSearch] = useState('')
  const [orgTags, setOrgTags] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  const trimmed = search.trim()
  const searchNorm = normalizeSearchText(trimmed)

  const available = useMemo(() => {
    const assignedIds = new Set(
      assignedTags
        .map(tag => tag.id)
        .filter(hasTagId)
        .map(String),
    )
    const assignedNames = new Set(
      assignedTags.map(tag => normalizeSearchText(tag.name)).filter(Boolean),
    )
    return orgTags.filter(tag => {
      const tagId = getOrgTagId(tag)
      if (hasTagId(tagId) && assignedIds.has(String(tagId))) return false
      if (assignedNames.has(normalizeSearchText(tag.name))) return false
      if (!searchNorm) return true
      return normalizeSearchText(tag.name).includes(searchNorm)
    })
  }, [orgTags, assignedTags, searchNorm])

  useEffect(() => {
    function onDocMouseDown(e) {
      if (popoverRef.current?.contains(e.target)) return
      if (anchorRef.current?.contains(e.target)) return
      onClose()
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [anchorRef, onClose])

  useEffect(() => {
    if (!tenantId) return undefined
    let cancelled = false
    const cached = peekCachedOrgTags(tenantId)
    if (cached) {
      setOrgTags(cached)
      setLoading(false)
      return undefined
    }
    setLoading(true)
    getCachedOrgTags(tenantId)
      .then(items => {
        if (!cancelled) setOrgTags(items)
      })
      .catch(err => {
        if (!cancelled) setOrgTags([])
        Sentry.captureException(err, { tags: { action: 'list_org_tags' }, extra: { tenantId } })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [tenantId])

  const pickTag = useCallback(async (tag) => {
    const tagId = getOrgTagId(tag)
    if (!tenantId || !contactId || !hasTagId(tagId)) return
    setBusy(true)
    try {
      await assignContactTag(tenantId, contactId, tagId)
      onAssigned({
        id: tagId,
        name: tag.name,
        slug: tag.slug ?? slugifyTagName(tag.name),
        color: tag.color ?? null,
      })
      onClose()
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'assign_contact_tag' }, extra: { tenantId, contactId, tagId } })
      alert(t('contactTags.assignFailed'))
    } finally {
      setBusy(false)
    }
  }, [tenantId, contactId, onAssigned, onClose, t])

  const dropStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    minWidth: 220,
    maxWidth: 280,
    background: 'white',
    border: '0.5px solid #e0e3f0',
    borderRadius: 8,
    zIndex: 200,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  }

  const inputStyle = variant === 'detail'
    ? 'w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500'
    : {
        width: '100%',
        padding: '8px 10px',
        fontSize: 12,
        border: 'none',
        borderBottom: '0.5px solid #f0f0f0',
        outline: 'none',
        boxSizing: 'border-box',
      }

  const rowStyle = {
    padding: '9px 12px',
    fontSize: 13,
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  }

  const optionRowStyle = {
    ...rowStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  }

  const optionPickButtonStyle = {
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    padding: 0,
    textAlign: 'left',
    color: 'inherit',
    font: 'inherit',
    cursor: busy ? 'not-allowed' : 'pointer',
  }

  return (
    <div ref={popoverRef} style={dropStyle} className={variant === 'detail' ? 'shadow-lg' : undefined}>
      {variant === 'detail' ? (
        <input
          className={inputStyle}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('contactTags.searchPlaceholder')}
          autoFocus
          disabled={busy}
        />
      ) : (
        <input
          style={inputStyle}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('contactTags.searchPlaceholder')}
          autoFocus
          disabled={busy}
        />
      )}
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {loading && (
          <div style={{ padding: '10px 12px', fontSize: 12, color: '#999' }}>{t('contactTags.assigning')}</div>
        )}
        {!loading && available.length === 0 && (
          <div style={{ padding: '10px 12px', fontSize: 12, color: '#999' }}>{t('contactTags.noResults')}</div>
        )}
        {available.map(tag => {
          const tagId = getOrgTagId(tag)
          const chip = getTagChipStyle(tag, variant)

          return (
            <div
              key={tagId ?? tag.name}
              onMouseDown={e => e.preventDefault()}
              style={optionRowStyle}
              onMouseEnter={e => { if (!busy) e.currentTarget.style.background = '#f5f7ff' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white' }}
            >
              <button
                type="button"
                disabled={busy}
                onClick={() => !busy && pickTag(tag)}
                style={optionPickButtonStyle}
              >
                {variant === 'detail' ? (
                  <span
                    className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-xs font-medium ${chip.className || ''}`}
                    style={chip.style}
                  >
                    <span className="truncate">{tag.name}</span>
                  </span>
                ) : (
                  <span
                    style={{
                      maxWidth: '100%',
                      padding: '2px 7px',
                      borderRadius: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      ...chip,
                    }}
                  >
                    <span className="truncate">{tag.name}</span>
                  </span>
                )}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TagChip({ tag, variant, onRemove, removing, disabled }) {
  const chip = getTagChipStyle(tag, variant)
  const canRemove = Boolean(tag.id) && !disabled

  if (variant === 'detail') {
    const style = chip.style || {}
    const className = chip.className || ''
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${className}`}
        style={style}
      >
        {tag.name}
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={removing}
            className="p-0.5 rounded-full hover:bg-black/10 disabled:opacity-50"
            aria-label="Remove tag"
          >
            <X size={12} />
          </button>
        )}
      </span>
    )
  }

  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 20,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        ...chip,
      }}
    >
      {tag.name}
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={removing}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: removing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            opacity: removing ? 0.5 : 1,
          }}
          aria-label="Remove tag"
        >
          <X size={10} />
        </button>
      )}
    </span>
  )
}

export default function ContactTagsPanel({
  tenantId,
  contactId,
  tags,
  onTagsChange,
  variant = 'inbox',
  disabled = false,
}) {
  const { t } = useTranslation()
  const assignBtnRef = useRef(null)
  const [orgTags, setOrgTags] = useState([])
  const [localTags, setLocalTags] = useState(() => enrichContactTags(tags, []))
  const [assignOpen, setAssignOpen] = useState(false)
  const [removingId, setRemovingId] = useState(null)

  const isDisabled = disabled || !tenantId || !contactId

  useEffect(() => {
    setLocalTags(enrichContactTags(tags, orgTags))
  }, [tags, orgTags])

  useEffect(() => {
    if (!tenantId) {
      setOrgTags([])
      return undefined
    }

    let cancelled = false
    const cached = peekCachedOrgTags(tenantId)
    if (cached) {
      setOrgTags(cached)
      return undefined
    }

    getCachedOrgTags(tenantId)
      .then(items => {
        if (!cancelled) setOrgTags(items)
      })
      .catch(err => {
        if (!cancelled) setOrgTags([])
        Sentry.captureException(err, { tags: { action: 'load_contact_tag_colors' }, extra: { tenantId } })
      })

    return () => { cancelled = true }
  }, [tenantId])

  const applyTags = useCallback((next) => {
    setLocalTags(next)
    onTagsChange?.(next)
  }, [onTagsChange])

  const handleAssigned = useCallback((tag) => {
    const next = normalizeContactTags(localTags, tag)
    applyTags(next)
  }, [localTags, applyTags])

  const handleUnassign = useCallback(async (tag) => {
    if (!tenantId || !contactId || !tag?.id) return
    const prev = localTags
    const next = localTags.filter(t => t.id !== tag.id)
    setLocalTags(next)
    setRemovingId(tag.id)
    try {
      await unassignContactTag(tenantId, contactId, tag.id)
      onTagsChange?.(next)
    } catch (err) {
      setLocalTags(prev)
      Sentry.captureException(err, { tags: { action: 'unassign_contact_tag' }, extra: { tenantId, contactId, tagId: tag.id } })
      alert(t('contactTags.unassignFailed'))
    } finally {
      setRemovingId(null)
    }
  }, [tenantId, contactId, localTags, onTagsChange, t])

  if (variant === 'detail') {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {t('contactTags.title')}
          </div>
          <div className="relative">
            <button
              ref={assignBtnRef}
              type="button"
              disabled={isDisabled}
              onClick={() => setAssignOpen(o => !o)}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#1535c8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              {t('contactTags.assign')}
            </button>
            {assignOpen && !isDisabled && (
              <AssignTagPopover
                tenantId={tenantId}
                contactId={contactId}
                assignedTags={localTags}
                onAssigned={handleAssigned}
                onClose={() => setAssignOpen(false)}
                anchorRef={assignBtnRef}
                variant={variant}
              />
            )}
          </div>
        </div>
        {localTags.length === 0 ? (
          <p className="text-xs text-gray-400">{t('contactTags.empty')}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {localTags.map(tag => (
              <TagChip
                key={tagKey(tag)}
                tag={tag}
                variant={variant}
                disabled={isDisabled}
                removing={removingId === tag.id}
                onRemove={() => handleUnassign(tag)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <p style={{ fontSize: 11, color: '#999', margin: 0 }}>{t('contactTags.title')}</p>
        <div style={{ position: 'relative' }}>
          <button
            ref={assignBtnRef}
            type="button"
            disabled={isDisabled}
            onClick={() => setAssignOpen(o => !o)}
            style={{
              fontSize: 10,
              padding: '2px 8px',
              border: '0.5px solid #e0e3f0',
              borderRadius: 6,
              background: 'white',
              color: '#1535c8',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Plus size={10} />
            {t('contactTags.assign')}
          </button>
          {assignOpen && !isDisabled && (
            <AssignTagPopover
              tenantId={tenantId}
              contactId={contactId}
              assignedTags={localTags}
              onAssigned={handleAssigned}
              onClose={() => setAssignOpen(false)}
              anchorRef={assignBtnRef}
              variant={variant}
            />
          )}
        </div>
      </div>
      {localTags.length === 0 ? (
        <p style={{ fontSize: 11, color: '#bbb', margin: 0 }}>{t('contactTags.empty')}</p>
      ) : (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {localTags.map(tag => (
            <TagChip
              key={tagKey(tag)}
              tag={tag}
              variant={variant}
              disabled={isDisabled}
              removing={removingId === tag.id}
              onRemove={() => handleUnassign(tag)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function tagKey(tag) {
  return tag.id != null ? String(tag.id) : `name:${tag.name}`
}

function getOrgTagId(tag) {
  return tag?.id ?? tag?.tag_id ?? null
}

function hasTagId(tagId) {
  return tagId != null && tagId !== ''
}
